import React, { useCallback, useEffect } from 'react'
import type { ServerError } from '@apollo/client'
import {
  usePartner,
  usePingAuth,
  usePartnerSettings
} from './helpers/hooks/auth'
import { parseJsonSilent } from '@@partner:helpers/util'

import { CustomProvider } from 'rsuite'
import AppRouterSwitch from './AppRouterSwitch'
import AppAuth from '@components/auth/AppAuth'
import AppLoader from '@components/common/AppLoader'
import AppConnectionError from '@components/common/AppConnectionError'
import OrderPaymentListener from './components/common/OrderPaymentListener'
import rsuiteLocaleRuRU from 'rsuite/locales/ru_RU'

import {
  UI_SETTING_COLOR_SCHEME,
  UI_SETTINGS_DEFAULT_COLOR_SCHEME
} from '@@partner:config/ui-settings'

import './styles/rsuite-theme-eda.scss'
import './styles/rsuite.less'

const isServerError = (networkError: unknown): networkError is ServerError => {
  return Boolean(networkError) && typeof networkError === 'object' && 'statusCode' in (networkError as ServerError)
}

const isIgnorableNetworkError = (networkError: unknown): boolean => {
  if (!networkError || typeof networkError !== 'object') {
    return false
  }

  const errorName = 'name' in networkError ? String((networkError as { name?: string }).name || '') : ''
  const errorMessage = 'message' in networkError
    ? String((networkError as { message?: string }).message || '').toLowerCase()
    : ''

  return (
    errorName === 'AbortError' ||
    errorName === 'CanceledError' ||
    errorMessage.includes('aborted') ||
    errorMessage.includes('cancel')
  )
}

function App () {
  const {
    isLoggedIn = false,
    setPartnerInfo,
    currentBrand,
    partnerInfo
  } = usePartner()
  const { getSetting } = usePartnerSettings()

  const { loading, currentPartnerInfo, error, refetchCurrentPartnerInfo } = usePingAuth()
  const handleAuth = useCallback(
    (accountInfo) => {
      const settings = parseJsonSilent(accountInfo?.settingsJson, {})
      // update partner info
      setPartnerInfo({ ...accountInfo, settings })
    },
    [ setPartnerInfo ]
  )

  useEffect(() => {
    if (!loading) {
      if (!error && currentPartnerInfo) {
        handleAuth(currentPartnerInfo)
      }
    }
  }, [ currentPartnerInfo, error, handleAuth, loading ])

  let isConnectionError = false

  if (error?.networkError && !isIgnorableNetworkError(error.networkError)) {
    if (isServerError(error.networkError)) {
      isConnectionError = !error.networkError.statusCode
    }
    else {
      isConnectionError = true
    }
  }

  // Do not block app with connection screen if auth payload is already available.
  const showConnectionError = !loading && isConnectionError && !currentPartnerInfo

  const handleRetryConnection = useCallback(() => {
    refetchCurrentPartnerInfo().catch(() => undefined)
  }, [ refetchCurrentPartnerInfo ])

  let content = null

  if (showConnectionError) {
    content = <AppConnectionError onRetry={handleRetryConnection} />
  }
  else if (isLoggedIn) {
    content = <AppRouterSwitch />
  }
  else {
    content = <AppAuth onAuth={handleAuth} />
  }

  return (
    <CustomProvider
      theme={getSetting(
        UI_SETTING_COLOR_SCHEME,
        UI_SETTINGS_DEFAULT_COLOR_SCHEME
      )}
      locale={rsuiteLocaleRuRU}
    >
      {/* <AppLoader /> */}
      <div className="App">
        {loading && <AppLoader />}
        {content}

        {partnerInfo.assembler_id && currentBrand?.slug && !showConnectionError && (
          <OrderPaymentListener
            receiverId={partnerInfo.assembler_id}
            brandSlug={currentBrand.slug}
          />
        )}
      </div>
    </CustomProvider>
  )
}

export default App
